import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';

import {
  ContactCategory,
  ContactForm,
  IStrapiDocument,
} from 'src/app/interfaces/strapi';
import { ApiService } from 'src/app/services/api.service';

import StrapiService from 'src/app/services/strapi.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { Regexes } from 'src/app/shared/regexes';
import { NgIf, NgFor } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { TooltipComponent } from '../../../shared/components/tooltip/tooltip.component';
import { SuccessBlockComponent } from '../../../shared/components/success-block/success-block.component';
import { ErrorBlockComponent } from '../../../shared/components/error-block/error-block.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { TooltipModalComponent } from '../../../shared/components/tooltip-modal/tooltip-modal.component';

@Component({
    selector: 'app-contact-form',
    templateUrl: './contact-form.component.html',
    styleUrls: ['./contact-form.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        BreadcrumbsComponent,
        FormsModule,
        ReactiveFormsModule,
        TooltipComponent,
        NgFor,
        SuccessBlockComponent,
        ErrorBlockComponent,
        ModalComponent,
        TooltipModalComponent,
    ],
})
export class ContactFormComponent extends PageComponent implements OnInit {
  public override pageData: ContactForm = <ContactForm>{};

  public contactForm!: UntypedFormGroup;

  public submitted = false;

  public contactSuccess = false;

  public contactError = false;

  public contactErrorMessage: string | null = null;

  public messageCounter = 0;

  public showTooltipModal = false;

  public tooltipModalContent = '';

  private cdr = inject(ChangeDetectorRef);

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    public readonly formBuilder: UntypedFormBuilder,
    private readonly api: ApiService,
    public readonly auth: AuthService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    // endpoint appears to be behind sanctum, so let's make sure the csrf token is set
    this.auth.sanctumCSRF().subscribe({
      next: () => {
        this.auth.csrfLoaded = true;
      },
    });

    this.strapi
      .getDocumentsByType('contact-form', { populate: '*' })
      .subscribe((data: IStrapiDocument<ContactForm>) => {
        this.parsePageData(data.data.attributes);
        super.setMetas();
        this.cdr.markForCheck();
      });

    this.contactForm = this.formBuilder.group({
      category: ['', [Validators.required.bind(this)]],
      title: ['', [Validators.required.bind(this)]],
      message: ['', [Validators.required.bind(this)]],
    });

    if (!this.auth.isAuthenticated()) {
      this.contactForm.addControl(
        'email',
        new UntypedFormControl('', {
          validators: [
            Validators.email.bind(this),
            Validators.required.bind(this),
            Validators.pattern(Regexes.emailRegex),
          ],
          updateOn: 'blur',
        })
      );
    } else {
      this.contactForm.addControl(
        'email',
        new UntypedFormControl(this.auth.currentUser?.email)
      );
    }

    this.contactForm.get('category')?.valueChanges.subscribe({
      next: () => {
        this.submitted = false;
      },
    });

    this.contactForm.get('title')?.valueChanges.subscribe({
      next: () => {
        this.submitted = false;
      },
    });

    this.contactForm.get('message')?.valueChanges.subscribe({
      next: (value: string) => {
        this.submitted = false;
        this.messageCounter = Number(value.length);
      },
    });
  }

  protected override parsePageData(page: ContactForm): void {
    super.parsePageData(page);

    this.pageData = page;
  }

  public get formControl(): {
    [key: string]: AbstractControl;
  } {
    return this.contactForm.controls;
  }

  public submit(e: Event): void {
    e.preventDefault();
    this.submitted = true;
    if (this.contactForm.valid) {
      const category = String(this.contactForm.get('category')?.value);
      const title = String(this.contactForm.get('title')?.value);
      const message = String(this.contactForm.get('message')?.value);
      const email = String(this.contactForm.get('email')?.value);
      if (this.auth.csrfLoaded) {
        this.api.contact(category, title, message, email).subscribe({
          next: () => {
            this.contactSuccess = true;
            this.contactError = false;
            this.cdr.markForCheck();
          },
          error: () => {
            this.contactSuccess = false;
            this.contactError = true;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.contactSuccess = false;
        this.contactError = true;
        this.contactErrorMessage =
          'There was a CSRF problem submitting your message. Please reload the page and try again.';
      }
    }
  }

  public shouldDisplayCategory(category: ContactCategory): boolean {
    if (category.logged_in_only) {
      return this.auth.isAuthenticated();
    }

    if (category.logged_out_only) {
      return !this.auth.isAuthenticated();
    }

    return true;
  }

  public openTooltip(id: string) {
    this.showTooltipModal = true;

    type PageDataIndex = keyof typeof this.pageData;

    const currentRoute = id as PageDataIndex;
    this.tooltipModalContent = this.pageData[currentRoute] as string;
  }
}
